import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap, take } from 'rxjs';
import { BearerHttpService } from '../bearer-http';
import { DAL_OPTIONS } from '../dal.module';
import { AUTH_SERVICE_TOKEN } from '../persons';
import { AnalyticsServiceInterface } from './analytics.service.interface';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements AnalyticsServiceInterface {
  private bearerHttpService = inject(BearerHttpService);
  private apiBase = inject(DAL_OPTIONS).eventBroker;
  private authService = inject(AUTH_SERVICE_TOKEN);

  /**
   * Send events using navigator.sendBeacon for reliable, performant delivery
   * Falls back to HTTP POST if sendBeacon is not available
   */
  sendEvents(events: unknown[], category?: 'ab-test' | 'user-journey'): Observable<{ message: string }> {
    const url = `${this.apiBase}/event/${category}`;

    return this.sendEventsWithBeaconOrPost(url, events);
  }

  sendEventImmediate(event: unknown, category?: 'ab-test' | 'user-journey'): Observable<{ message: string }> {
    const url = `${this.apiBase}/event/${category}`;

    return this.sendEventsWithBeaconOrPost(url, [event]);
  }

  isServiceAvailable(): Observable<boolean> {
    return of(true);
  }

  /**
   * Send events using sendBeacon with bearer token included in the body
   * sendBeacon doesn't support custom headers, so we include auth in the payload
   */
  private sendEventsWithBeaconOrPost(url: string, events: unknown[]): Observable<{ message: string }> {
    return this.authService.getAccessToken$().pipe(
      take(1),
      switchMap((accessToken) => {
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          const token = `Bearer ${accessToken.userId}:${accessToken.id}`;

          // Create payload with auth token and events
          const payload = {
            auth: token,
            events: events,
          };

          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          const success = navigator.sendBeacon(url, blob);

          // sendBeacon returns boolean, convert to Observable
          if (success) {
            return of({ message: 'Events queued for delivery' });
          }
        }

        // If sending with beacon fails (queue if full or not supported), try post
        return this.bearerHttpService
          .post<{ message: string }>(url, { events })
          .pipe(map(() => ({ message: 'Events queued for delivery' })));
      })
    );
  }
}
